/**
 * Pure tick-state -> `excluded` helper (Task 4c fix round 1, 2026-08-05).
 *
 * CustomerOnboardingPage.handleConfirmWrite — and, once built, the Phase 2
 * review wizard — both need to turn "which rows are ticked" into "which
 * original indices to tell the server to exclude": the complement, over the
 * FULL `proposal.prerequisites` / `proposal.dependents` array, of the kept
 * (ticked) set.
 *
 * Getting that complement wrong (off-by-one, a stale total, the wrong Set)
 * would still produce IN-RANGE, non-duplicate indices — a perfectly valid
 * `excluded` payload as far as the server's `InvalidExclusionError` guard
 * is concerned, since that guard validates FORM (range + duplicates), not
 * INTENT. A wrong-but-well-formed complement would silently exclude or keep
 * the wrong rows: exactly the `__prereq_index:N__` silent-corruption class
 * Task 4c exists to eliminate. Extracted here — with its own unit tests in
 * `__tests__/exclusions.test.js` — so the logic exists in exactly one
 * place, and the upcoming wizard reuses it instead of re-deriving it.
 */

/**
 * @param {number} totalCount - length of the FULL array (`proposal.prerequisites`
 *   or `proposal.dependents`) the indices are positions in.
 * @param {Set<number>|number[]} keptIndices - indices the operator left
 *   ticked. Members outside `[0, totalCount)` are IGNORED (see below), not
 *   thrown on.
 * @returns {number[]} every index in `[0, totalCount)` NOT present in
 *   `keptIndices`, ascending.
 *
 * Out-of-range design choice: `keptIndices` originates from antd Table's
 * `selectedRowKeys`, UI state the table itself keeps in sync with
 * `dataSource`. An entry outside range would mean a bug upstream of this
 * function, not something this pure function should throw over — and since
 * such an entry was never a candidate for exclusion anyway, ignoring it has
 * no effect on the result. Range/duplicate correctness of the eventual
 * wire payload is enforced server-side by `InvalidExclusionError`
 * (`Agent-Backend/src/onboarding/orchestrator.py`); this function's job is
 * only to compute the correct complement from valid input.
 */
export function computeExcluded(totalCount, keptIndices) {
  const kept = keptIndices instanceof Set ? keptIndices : new Set(keptIndices);
  const excluded = [];
  for (let i = 0; i < totalCount; i += 1) {
    if (!kept.has(i)) excluded.push(i);
  }
  return excluded;
}
